// @ts-strict-ignore
import * as React from 'react';
import { isMobileOnly } from 'react-device-detect';
import { useSelector } from 'react-redux';
import { Redirect, Route, RouteProps, useRouteMatch } from 'react-router-dom';
import { getAllowedMobileRoutes, getIdFromAppId, getIdFromOrgId } from '../helpers';
import { createAppBuildsRoute, createAppReleasesRoute } from '../helpers/routeHelpers';
import { RootState } from '../reducers/reducers';
import routes from '../routes';
import { AppRouteComponentMatchParams, RedirectState } from '../typescriptHelpers';
import { selectAppSettings } from '@/selectors/selectors';

interface PrivateAppRouteProps extends RouteProps {
    defaultSelectedAppIdParam?: string;
    defaultSelectedOrgIdParam?: string;
}

const PrivateAppRoute: React.FC<PrivateAppRouteProps> = (props: PrivateAppRouteProps) => {
    const { render, location, defaultSelectedAppIdParam, defaultSelectedOrgIdParam, ...rest } = props;

    const allowedMobileRoutes = getAllowedMobileRoutes();

    const match = useRouteMatch(allowedMobileRoutes);
    const matchAppRoute = useRouteMatch<AppRouteComponentMatchParams>({ path: routes.app, exact: true });
    const routeMatchesAllowedMobileRoute = !!match;

    const isLoggedIn = useSelector((state: RootState) => state.auth.isLoggedIn);
    const is2FARequired = useSelector((state: RootState) => state.auth.twoFactorAuthRequired);
    const isEmailVerificationRequired = useSelector((state: RootState) => !state.auth.isEmailVerified);
    const isStateFetched = useSelector((state: RootState) => state.user !== null);
    const appShouldDefaultToBuildDistro = useSelector((state: RootState) => {
        const orgIdParamToUse = matchAppRoute?.params.orgIdParam ?? defaultSelectedOrgIdParam;
        const appIdParamToUse = matchAppRoute?.params.appIdParam ?? defaultSelectedAppIdParam;

        if (isStateFetched && orgIdParamToUse && appIdParamToUse) {
            const orgId = getIdFromOrgId(orgIdParamToUse, state);
            const appId = getIdFromAppId(orgId, appIdParamToUse, state);
            if (orgId && appId) {
                const appSettings = selectAppSettings(state, appId);

                // defensive handling here in case selector executes during an app deletion event before the parent props have been passed down
                if (appSettings) {
                    return appSettings.shouldDefaultToBuildDistro;
                }

                return false;
            }
        }

        return false;
    });

    if (is2FARequired) {
        const state: RedirectState = { fromLocation: location };
        console.debug(
            '[REDIRECT DEBUG]',
            'current route:',
            location.pathname,
            '-> next route:',
            routes.twoFactorAuth,
            '(2FA required)',
        );
        return <Redirect to={{ pathname: routes.twoFactorAuth, state: state }} />;
    }

    if (!isLoggedIn) {
        const redirectRoute = routes.login;

        const state: RedirectState = { fromLocation: location };
        console.debug(
            '[REDIRECT DEBUG]',
            'current route:',
            location.pathname,
            '-> next route:',
            redirectRoute,
            '(not logged in)',
        );
        return <Redirect to={{ ...location, pathname: redirectRoute, state: state }} />;
    }

    if (isEmailVerificationRequired) {
        const state: RedirectState = { fromLocation: location };
        console.debug(
            '[REDIRECT DEBUG]',
            'current route:',
            location.pathname,
            '-> next route:',
            routes.verifyEmail,
            '(email verification required)',
        );
        return <Redirect to={{ ...location, pathname: routes.verifyEmail, state: state }} />;
    }

    if (isLoggedIn && !isStateFetched) {
        return null;
    }

    // redirect disallowed mobile routes
    if (isLoggedIn && !isEmailVerificationRequired && isMobileOnly && !routeMatchesAllowedMobileRoute) {
        const defaultMobileRoute =
            defaultSelectedOrgIdParam && defaultSelectedAppIdParam ?
                createAppBuildsRoute(defaultSelectedOrgIdParam, defaultSelectedAppIdParam)
            :   routes.mobile;
        console.debug(
            '[REDIRECT DEBUG]',
            'current route:',
            location.pathname,
            '-> next route:',
            defaultMobileRoute,
            '(mobile route not allowed)',
        );
        return <Redirect to={{ ...location, pathname: defaultMobileRoute }} />;
    }

    const isDashboardHomeOrAppRoute =
        location.pathname === routes.dashboard || location.pathname === routes.home || !!matchAppRoute;

    // redirect to default app route
    if (isLoggedIn && isStateFetched && isDashboardHomeOrAppRoute) {
        if (defaultSelectedOrgIdParam && matchAppRoute) {
            const appRouteParams = matchAppRoute.params;

            const resolvedReleasesRoute = createAppReleasesRoute(appRouteParams.orgIdParam, appRouteParams.appIdParam);
            const resolvedBDRoute = createAppBuildsRoute(appRouteParams.orgIdParam, appRouteParams.appIdParam);

            const redirectTo = {
                ...location,
                pathname: appShouldDefaultToBuildDistro ? resolvedBDRoute : resolvedReleasesRoute,
            };
            console.debug(
                '[REDIRECT DEBUG]',
                'current route:',
                location.pathname,
                '-> next route:',
                redirectTo.pathname,
                '(app route redirect - matched app route)',
            );
            return <Redirect to={redirectTo} />;
        } else if (defaultSelectedOrgIdParam && defaultSelectedAppIdParam) {
            const resolvedReleasesRoute = createAppReleasesRoute(defaultSelectedOrgIdParam, defaultSelectedAppIdParam);
            const resolvedBDRoute = createAppBuildsRoute(defaultSelectedOrgIdParam, defaultSelectedAppIdParam);

            const redirectTo = {
                ...location,
                pathname: appShouldDefaultToBuildDistro ? resolvedBDRoute : resolvedReleasesRoute,
            };
            console.debug(
                '[REDIRECT DEBUG]',
                'current route:',
                location.pathname,
                '-> next route:',
                redirectTo.pathname,
                '(app route redirect - default selected)',
            );
            return <Redirect to={redirectTo} />;
        } else {
            return <Route {...rest} render={render} />;
        }
    } else {
        return <Route {...rest} render={render} />;
    }
};

export default PrivateAppRoute;
